import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';
import { Recipe, RecipeIngredient } from 'public-contracts';

export interface RecipeCreateEntry {
  name: string;
  description: string;
  imageId: string;
  productCategoryId?: string;
  ingredients: RecipeIngredient[];
  products?: string[];
}

export interface RecipeResponse {
  success: boolean;
  data: Recipe;
  message?: string;
}

export interface RecipeListResponse {
  success: boolean;
  data: Recipe[];
  total?: number;
}

export interface RecipeValidationResponse {
  success: boolean;
  data: {
    valid: boolean;
    errors: string[];
    missingMappings: Array<{
      ingredientId: string;
      ingredientName?: string;
    }>;
    nestingLevel?: {
      maxLevel: number;
      valid: boolean;
      errors: string[];
      path: string[];
    };
  };
}

export interface RecipeNestingLevelResponse {
  success: boolean;
  data: {
    maxLevel: number;
    valid: boolean;
    errors: string[];
    path: string[];
  };
}

export interface RecipeCostResponse {
  success: boolean;
  data: {
    recipeId: string;
    recipeName?: string;
    totalCost: number;
    currency: string;
    breakdown: Array<{
      ingredientId: string;
      ingredientName?: string;
      amount: number;
      unit: string;
      inventoryId: string;
      inventoryName?: string;
      unitPrice: number;
      totalCost: number;
    }>;
    calculatedAt: string;
  };
}

export interface RecipeAvailabilityResponse {
  success: boolean;
  data: Array<{
    ingredientId: string;
    ingredientName?: string;
    requiredAmount: number;
    unit: string;
    available: boolean;
    availableAmount: number;
    inventoryId?: string;
    error?: string;
  }>;
}

class RecipesService {
  private apiRoot = configs.apiRoot;

  async getRecipes(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<
    Recipe[] & { __total?: number; __page?: number; __perPage?: number }
  > {
    const queryParams = new URLSearchParams();
    const page = params?.page ?? 1;
    // Support both size and perPage parameters, default to 20
    let size = 20;
    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    }

    // CRITICAL: API requires 'size' parameter, not 'perPage'
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());
    if (params?.q) queryParams.append('q', params.q);
    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/recipes${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      let errorMessage = `Failed to fetch recipes: ${response.statusText}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } else {
          // Handle HTML error pages
          const text = await response.text();
          errorMessage = `Server error (${response.status}): ${text.substring(
            0,
            200
          )}`;
        }
      } catch (e) {
        // If parsing fails, use the default error message
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    let recipes: Recipe[] = [];

    // Handle both response formats: { success: true, data: [...] } or direct array
    if (Array.isArray(result)) {
      recipes = result;
    } else {
      recipes = result.data || [];
    }

    // Check Content-Range header for total count
    const contentRange = response.headers.get('Content-Range');
    let total = recipes.length;

    if (contentRange) {
      // Content-Range format: "items 0-19/100"
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    // Attach metadata to the array
    const resultWithMeta = recipes as Recipe[] & {
      __total?: number;
      __page?: number;
      __perPage?: number;
    };
    resultWithMeta.__total = total;
    resultWithMeta.__page = page;
    resultWithMeta.__perPage = size;

    return resultWithMeta;
  }

  async getRecipe(id: string): Promise<Recipe> {
    const response = await authenticatedFetch(`${this.apiRoot}/recipes/${id}`, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = `Failed to fetch recipe: ${response.statusText}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } else {
          const text = await response.text();
          errorMessage = `Server error (${response.status}): ${text.substring(0, 200)}`;
        }
      } catch (e) {
        // If parsing fails, use the default error message
        errorMessage = `Failed to fetch recipe: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    // Handle both response formats: { success: true, data: {...} } or direct object
    if (result.data) {
      return result.data;
    } else if (result.id) {
      // Direct recipe object
      return result as Recipe;
    } else {
      throw new Error('Invalid recipe response format');
    }
  }

  async createRecipe(data: RecipeCreateEntry): Promise<Recipe> {
    const response = await authenticatedFetch(`${this.apiRoot}/recipes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to create recipe: ${response.statusText}`
      );
    }

    const result: RecipeResponse = await response.json();
    return result.data;
  }

  async updateRecipe(
    id: string,
    data: Partial<RecipeCreateEntry>
  ): Promise<Recipe> {
    const response = await authenticatedFetch(`${this.apiRoot}/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to update recipe: ${response.statusText}`
      );
    }

    const result: RecipeResponse = await response.json();
    return result.data;
  }

  async deleteRecipe(id: string): Promise<void> {
    const response = await authenticatedFetch(`${this.apiRoot}/recipes/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to delete recipe: ${response.statusText}`
      );
    }
  }

  async validateRecipe(id: string): Promise<RecipeValidationResponse['data']> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/recipes/${id}/validate`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to validate recipe: ${response.statusText}`
      );
    }

    const result: RecipeValidationResponse = await response.json();
    return result.data;
  }

  async getRecipeCost(id: string): Promise<RecipeCostResponse['data']> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/recipes/${id}/cost`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to get recipe cost: ${response.statusText}`
      );
    }

    const result: RecipeCostResponse = await response.json();
    return result.data;
  }

  async getRecipeCostBreakdown(
    id: string
  ): Promise<RecipeCostResponse['data']['breakdown']> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/recipes/${id}/cost-breakdown`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message ||
          `Failed to get recipe cost breakdown: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data;
  }

  async getRecipeAvailability(
    id: string
  ): Promise<RecipeAvailabilityResponse['data']> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/recipes/${id}/availability`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message ||
          `Failed to get recipe availability: ${response.statusText}`
      );
    }

    const result: RecipeAvailabilityResponse = await response.json();
    return result.data;
  }

  async checkRecipeNestingLevel(
    id: string
  ): Promise<RecipeNestingLevelResponse['data']> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/recipes/${id}/nesting-level`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message ||
          `Failed to check recipe nesting level: ${response.statusText}`
      );
    }

    const result: RecipeNestingLevelResponse = await response.json();
    return result.data;
  }
}

export const recipesService = new RecipesService();
